import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Invite, InviteType } from '../../types/invites';
import { authenticationService, invitationService, profileService } from '../../services';
import { ProfileDataType, ProfileField } from '../../types/profile';
import { createEmptyField } from '../../utils/formatUtils';
import ProfileFieldForm from '../../components/forms/profile/ProfileFieldForm';
import { sortProfileFields } from '../../utils/sortUtils';
import { isEmptyField, isValidProfileFields } from '../../utils/validationUtils';
import { Language } from '../../types/requests';
import { UserAccountCreationEvent, UserAccountCreationChannel } from '../../analytics/models';
import { LoginPageState, SuccessPageState } from '../../types/navigation';
import NameInput from '../../components/forms/profile/NameInput';
import { joinWithAnd, profileDataTypeToString } from '../../utils/stringUtils';
import TermsAndConditions from '../../components/misc/TermsAndConditions';
import EmailInput from '../../components/forms/profile/EmailInput';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import Button from '../../components/misc/Button';
import AccountExistsModal from '../../components/modals/AccountExistsModal';
import useMagicLogin from '../../hooks/useMagicLogin';
import useDeleteParams from '../../hooks/useDeleteParams';

export default function UnauthenticatedInvitePage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  if (useMagicLogin()) {
    return <></>;
  }

  useDeleteParams('link');

  const invitationId = params.get('invitationId');
  const link = params.get('link');

  const [invite, setInvite] = useState<Invite>();
  const [alias, setAlias] = useState<string>('');
  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);
  const [accountExists, setAccountExists] = useState<boolean>(false);
  const [accountExistsModal, setAccountExistsModal] = useState<boolean>(false);
  const [terms, setTerms] = useState<boolean>(false);
  const [validate, setValidate] = useState<boolean>(false);

  const emailField = profileFields.find((x) => x.dataType === ProfileDataType.EMAIL);

  useEffect(() => {
    if (!invitationId) {
      toast.error(t('toast.error.general.invalidInvite'));
      navigate('/');
    }
  }, []);

  useLayoutEffect(() => {
    if (!invitationId || link) return;

    const setFields = async () => {
      const inv =
        (location.state?.invite as Invite) ||
        (await invitationService.getInvite(invitationId).catch(() => {
          toast.error(t('toast.error.general.inviteNotFound'));
          navigate('/');
        }));

      if (!inv) return;
      setInvite(inv);

      // If dataRequest is empty, use default fields: Email, Phone Number, Address, and Birthdate
      const defaultDataRequest = [
        ProfileDataType.EMAIL,
        ProfileDataType.PHONENUMBER,
        ProfileDataType.ADDRESS,
        ProfileDataType.BIRTHDATE,
      ];

      const dataRequest =
        inv.propertyTypeRequested.length === 0
          ? defaultDataRequest
          : Array.from(new Set([...inv.propertyTypeRequested, ProfileDataType.EMAIL]));

      setProfileFields(sortProfileFields(dataRequest.map((dr) => createEmptyField(dr))));
    };

    setFields();
  }, []);

  const validateFields = (): boolean => {
    if (!alias || !alias.split('+')[0] || !alias.split('+')[1]) {
      toast.error(t('toast.error.field.name'));
      return false;
    }
    if (!terms) {
      toast.error(t('toast.error.general.terms'));
      return false;
    }
    if (isEmptyField(profileFields.find((x) => x.dataType === ProfileDataType.EMAIL)!)) {
      toast.error(t('toast.error.field.email'));
      return false;
    }
    return isValidProfileFields(profileFields);
  };

  const handleSave = async () => {
    setValidate(true);
    if (!validateFields()) return;
    const data = profileFields.filter((x) => !isEmptyField(x));
    const email = profileFields.find((x) => x.dataType === ProfileDataType.EMAIL)!.email!;

    const exists = await profileService.checkEmailExists(email);
    if (exists) {
      setAccountExists(true);
      setAccountExistsModal(true);
      return;
    }

    const event: UserAccountCreationEvent = {
      channel: UserAccountCreationChannel[UserAccountCreationChannel.DEFAULT],
      details: undefined,
    };
    const options = authenticationService.mapInstantRegisterToRegisterOptions({
      invitationId: invitationId!,
      inviteType: InviteType.USER,
      data,
      lang: Language.NL,
      customFieldResponses: undefined,
    });
    const login = await authenticationService.register(alias, email, event, options);

    const state: SuccessPageState = {
      translationKey: 'page.common.success.message.invite',
      translationOptions: {
        requesterName: requesterName || '',
      },
      requesterId: invite?.inviterId,
      requesterAlias: invite?.inviterAlias,
    };
    navigate('/success', { state });
    toast.success(t('toast.success.connectionCreated'));
    setTimeout(login, 1);
  };

  const handleLogin = async () => {
    const state: LoginPageState = {
      email: emailField?.email || profileFields.find((x) => x.dataType === ProfileDataType.EMAIL)!.email!,
      invitationId: invitationId!,
      inviteType: InviteType.USER,
      invite: invite!,
    };
    navigate('/login', { state });
  };
  console.log('invite', invite);
  const requesterName = invite?.inviterAlias?.split('+')[0];
  const dataRequestEmpty = invite?.propertyTypeRequested.length === 0;

  const setProfileFieldByType = (field: ProfileField) => {
    setProfileFields(profileFields.map((f: ProfileField): ProfileField => (f.dataType === field.dataType ? field : f)));
  };

  return (
    <>
      <ResponsePageWrapper handleLogin={handleLogin}>
        <div className="flex flex-col">
          <h2 className="mb-4 text-2xl font-semibold">{t('page.shared.requestedData')}</h2>
          <p className="text-sm">
            {dataRequestEmpty
              ? t('page.invite.businessCardMessage', { name: requesterName })
              : // eslint-disable-next-line prefer-template
                t('page.invite.message', {
                  name: requesterName,
                  dataRequest: joinWithAnd(invite?.dataRequest.map(profileDataTypeToString) || []),
                }) +
                ' ' +
                t('page.invite.nameEmailMandatory')}
          </p>
        </div>
        <div className="mt-4 flex flex-col gap-4">
          <NameInput alias={alias} setAlias={setAlias} mandatory validate={validate} />

          {emailField && (
            <EmailInput
              field={emailField!}
              emailCheck
              validate={validate}
              setAccountExists={setAccountExists}
              setField={setProfileFieldByType}
              mandatory
            />
          )}
          {accountExists ? (
            <span className="text-sm text-error">
              {t('page.shared.emailInUse')}{' '}
              <strong className="cursor-pointer underline" onClick={handleLogin}>
                {t('page.shared.logInHere')}
              </strong>
              .
            </span>
          ) : (
            ''
          )}

          {profileFields.map((field) => {
            return field.dataType === ProfileDataType.EMAIL ? (
              <></>
            ) : (
              <div key={field.dataType} className={field.dataType === ProfileDataType.BIRTHDATE ? 'w-1/2' : ''}>
                <ProfileFieldForm field={field} canBeEmpty validate={validate} setField={setProfileFieldByType} />
              </div>
            );
          })}
        </div>
        <TermsAndConditions checked={terms} setChecked={setTerms} className="mt-4" />
        <div className="mt-4 flex h-full min-h-10 justify-end">
          <Button variant="primary" className="px-[32px]" onClick={handleSave}>
            {t('page.shared.acceptInvite')}
          </Button>
        </div>
      </ResponsePageWrapper>
      {emailField?.email && (
        <AccountExistsModal
          open={accountExistsModal}
          setOpen={setAccountExistsModal}
          request={{
            email: emailField?.email,
            invitationId: invitationId ?? undefined,
            inviteType: InviteType.USER,
          }}
        />
      )}
    </>
  );
}
